'use client'

import { createContext, ReactNode, useContext } from 'react'
import { RichTextContent } from '@/hooks/useRichText'
import { FooterSocialLink } from '@/components/SiteFooter/SocialLinks'
import { FooterLinkSectionProps } from '@/components/SiteFooter/LinkSection'

export interface HeaderLinkSection {
  sys: {
    id: string
  }
  fields: {
    title: string
    isDefault: boolean
    headerLinks: HeaderLink[]
    headerLinkSections?: HeaderLinkSection[]
    subtitle?: string
  }
}

export interface HeaderLink {
  sys: {
    type: string
    id: string
  }
  fields: {
    title: string
    url: string
    isNew: boolean
  }
}

export interface LayoutData {
  header: {
    fields: {
      logo: {
        fields: {
          title: string
          description: string
          file: {
            url: string
            details: {
              size: number
              image: {
                width: number
                height: number
              }
              fileName: string
              contentType: string
            }
          }
        }
      }
      headerLinkSections: HeaderLinkSection[]
      aboveFoldExperimentation: boolean
      showLoginButton: boolean
      loginButtonText: string
      showOpenAccountButton: boolean
      openAccountButtonText: string
    }
  }
  footer: {
    fields: {
      copyrightOwner: string
      footerLinkSections: FooterLinkSectionProps[]
      logo: {
        fields: {
          title: string
          file: {
            url: string
            details: {
              image: {
                width: number
                height: number
              }
            }
          }
        }
      }
      legalDisclaimer: RichTextContent
      signOffText: string
      footerSocialLinks: FooterSocialLink[]
    }
  }
}

const LayoutDataContext = createContext<LayoutData | undefined>(undefined)

export const useLayoutData = () => {
  const context = useContext(LayoutDataContext)
  if (!context)
    throw new Error('useLayoutData must be used within LayoutDataProvider')
  return context
}

export const LayoutDataProvider = ({
  value,
  children,
}: {
  value: LayoutData
  children: ReactNode
}) => (
  <LayoutDataContext.Provider value={value}>
    {children}
  </LayoutDataContext.Provider>
)
